import { useTranslation } from 'next-i18next/pages';

import { Form, Select } from 'antd';

import { TranslationNamespace } from '@/i18n';

import type { YesNo } from '../../../constants';
import { CompactInputNumber } from '../CompactInputNumber';
import { StepActions } from '../StepActions';

import styles from '@/components/TariffCalculatorNew/TariffCalculator.module.scss';

interface StepCostsProps {
	onPrev: () => void;
	onNext: () => void;
	hasDeferred: YesNo;
	hasSite: YesNo;
	hasMarketer: YesNo;
}

export function StepCosts({ onPrev, onNext, hasDeferred, hasSite, hasMarketer }: StepCostsProps) {
	const { t } = useTranslation(TranslationNamespace.Calculator);
	const yesNoOptions = [
		{ value: 'yes' as YesNo, label: t('costs.yes') },
		{ value: 'no' as YesNo, label: t('costs.no') },
	];
	return (
		<section className={styles.stepPanel}>
			<h2 className={styles.stepTitle}>{t('costs.title')}</h2>
			<p className={styles.stepLead}>{t('costs.description')}</p>

			<h3 className={styles.sectionTitle}>{t('costs.storage-section')}</h3>
			<div className={styles.fieldGrid}>
				<Form.Item name="volume" label={t('costs.volume')}>
					<CompactInputNumber min={0} step={1} addon={t('units.tons-year')} size="large" />
				</Form.Item>
				<Form.Item name="avgPrice" label={t('costs.average-price')}>
					<CompactInputNumber min={0} step={1} addon={t('units.rub-kg')} size="large" />
				</Form.Item>
				<Form.Item name="storageDays" label={t('costs.storage-days')}>
					<CompactInputNumber min={0} step={1} addon={t('units.days')} size="large" />
				</Form.Item>
				<Form.Item name="storageCost" label={t('costs.storage-cost')}>
					<CompactInputNumber min={0} step={1} addon={t('units.rub-ton-day')} size="large" />
				</Form.Item>
				<Form.Item name="creditRate" label={t('costs.credit-rate')}>
					<CompactInputNumber
						min={0}
						max={100}
						step={0.1}
						addon={t('units.percent')}
						size="large"
					/>
				</Form.Item>
			</div>

			<h3 className={styles.sectionTitle}>{t('costs.deferred-section')}</h3>
			<div className={styles.fieldGrid}>
				<Form.Item name="hasDeferred" label={t('costs.has-deferred')}>
					<Select options={yesNoOptions} />
				</Form.Item>
				{hasDeferred === 'yes' && (
					<>
						<Form.Item name="deferredSales" label={t('costs.deferred-sales')}>
							<CompactInputNumber min={0} step={1000} addon={t('units.rub-year')} size="large" />
						</Form.Item>
						<Form.Item name="deferredDays" label={t('costs.deferred-days')}>
							<CompactInputNumber min={0} step={1} addon={t('units.days')} size="large" />
						</Form.Item>
						<Form.Item name="badDebt" label={t('costs.bad-debt')}>
							<CompactInputNumber min={0} step={1000} addon={t('units.rub-year')} size="large" />
						</Form.Item>
					</>
				)}
			</div>

			<h3 className={styles.sectionTitle}>{t('costs.loss-section')}</h3>
			<div className={styles.fieldGrid}>
				<Form.Item name="expiredTons" label={t('costs.expired')}>
					<CompactInputNumber min={0} step={0.1} addon={t('units.tons-year')} size="large" />
				</Form.Item>
				<Form.Item name="expiredLoss" label={t('costs.expired-loss')}>
					<CompactInputNumber min={0} step={1} addon={t('units.rub-kg')} size="large" />
				</Form.Item>
				<Form.Item name="markdownTons" label={t('costs.markdown-volume')}>
					<CompactInputNumber min={0} step={0.1} addon={t('units.tons-year')} size="large" />
				</Form.Item>
				<Form.Item name="markdownLoss" label={t('costs.markdown-loss')}>
					<CompactInputNumber min={0} step={1} addon={t('units.rub-kg')} size="large" />
				</Form.Item>
				<Form.Item name="weightDiff" label={t('costs.weight-difference')}>
					<CompactInputNumber
						min={0}
						max={100}
						step={0.1}
						addon={t('units.percent')}
						size="large"
					/>
				</Form.Item>
				<Form.Item name="returns" label={t('costs.returns')}>
					<CompactInputNumber min={0} step={1000} addon={t('units.rub-year')} size="large" />
				</Form.Item>
			</div>

			<h3 className={styles.sectionTitle}>{t('costs.marketing-section')}</h3>
			<div className={styles.fieldGrid}>
				<Form.Item name="hasSite" label={t('costs.has-site')}>
					<Select options={yesNoOptions} size="large" />
				</Form.Item>
				{hasSite === 'yes' ? (
					<Form.Item name="siteSupport" label={t('costs.site-support')}>
						<CompactInputNumber min={0} step={1000} addon={t('units.rub-month')} size="large" />
					</Form.Item>
				) : (
					<Form.Item name="b2bDevelopment" label={t('costs.b2b')} extra={t('costs.b2b-note')}>
						<CompactInputNumber min={0} step={1000} addon={t('units.rub-once')} size="large" />
					</Form.Item>
				)}
				<Form.Item name="hasMarketer" label={t('costs.has-marketer')}>
					<Select options={yesNoOptions} />
				</Form.Item>
				{hasMarketer === 'yes' && (
					<Form.Item name="marketerCost" label={t('costs.marketer')}>
						<CompactInputNumber min={0} step={1000} addon={t('units.rub-month')} size="large" />
					</Form.Item>
				)}
				<Form.Item name="adBudget" label={t('costs.ad-budget')}>
					<CompactInputNumber min={0} step={1000} addon={t('units.rub-month')} size="large" />
				</Form.Item>
				<Form.Item name="articles" label={t('costs.articles')}>
					<CompactInputNumber min={0} step={1} addon={t('units.pcs-year')} size="large" />
				</Form.Item>
				<Form.Item name="articleCost" label={t('costs.article-cost')}>
					<CompactInputNumber min={0} step={1000} addon="₽" size="large" />
				</Form.Item>
				<Form.Item name="aiMinutes" label={t('costs.ai-data')}>
					<CompactInputNumber min={0} step={1} addon={t('units.minutes-day')} size="large" />
				</Form.Item>
				<Form.Item name="exhibitions" label={t('costs.exhibitions')}>
					<CompactInputNumber min={0} step={1} addon={t('units.pcs-year')} size="large" />
				</Form.Item>
				<Form.Item name="exhibitionCost" label={t('costs.exhibition-cost')}>
					<CompactInputNumber min={0} step={1000} addon="₽" size="large" />
				</Form.Item>
			</div>

			<StepActions onPrev={onPrev} onNext={onNext} />
		</section>
	);
}
